import React, { useEffect, type FC } from "react";

import { useIntl } from "react-intl";
import { useNavigate } from "react-router-dom";
import { useRecoilCallback, useRecoilValue } from "recoil";

import styled from "@emotion/styled";

import {
  DataListRow,
  EmptyState,
  Flex,
  Grid,
  GridCell,
  NoResults,
  Pagination,
  SKELETON_LOADER_VARIANT_ONWHITE,
  SkeletonLoader,
} from "mailup-web-components";

import useActivityStream from "Hooks/useActivityStream";

import type { SearchResult } from "Pages/RecipientsList/model";

import RenderWhen from "Components/RenderWhen";
import ActivityStreamItem from "Pages/RecipientsList/components/aside/activityStream/ActivityStreamItem";
import DataRetentionMessage from "Pages/RecipientsList/components/aside/activityStream/DataRetentionMessage";
import Filters from "Pages/RecipientsList/components/aside/activityStream/Filters";
import {
  initActivityStreamFiltersCallback,
  resetActivityStreamFiltersCallback,
} from "Pages/RecipientsList/recoil/callbacks/recipient";
import { activityFiltersToEventTypes } from "Pages/RecipientsList/recoil/services/recipient";
import { recipientActivityStreamFiltersState } from "Pages/RecipientsList/recoil/state/recipient";

const PAGE_SIZE = 10;

const StyledSection = styled.section`
  & > div:first-of-type {
    border-top: 0;
  }
`;

interface TabActivityStreamProps {
  item: SearchResult;
  channel: "email" | "sms";
}

const TabActivityStream: FC<TabActivityStreamProps> = ({ item, channel }) => {
  const intl = useIntl();

  const navigate = useNavigate();

  const isEmptyState =
    channel === "email"
      ? item.EmailSubscriptionStatus === undefined
      : item.SMSSubscriptionStatus === undefined;

  const channelStatus =
    channel === "email"
      ? item.EmailSubscriptionStatus
      : item.SMSSubscriptionStatus;

  const { allFilters, filters } = useRecoilValue(
    recipientActivityStreamFiltersState,
  );

  const initActivityFilters = useRecoilCallback(
    initActivityStreamFiltersCallback,
    [],
  );

  const resetActivityFilters = useRecoilCallback(
    resetActivityStreamFiltersCallback,
    [],
  );

  const { value, fetchResults, cleanup } = useActivityStream(item.Id, channel);

  useEffect(() => {
    if (!isEmptyState) {
      void initActivityFilters(channel);

      void fetchResults(
        1,
        PAGE_SIZE,
        activityFiltersToEventTypes(true, [], channel),
      );
    }

    return () => {
      void resetActivityFilters();
      cleanup();
    };
  }, [
    channel,
    isEmptyState,
    resetActivityFilters,
    fetchResults,
    initActivityFilters,
    cleanup,
  ]);

  const hasNoResults = !isEmptyState && value.totalItems === 0 && value.loaded;

  const handlePageChange = (p: number): void => {
    void fetchResults(
      p,
      PAGE_SIZE,
      activityFiltersToEventTypes(allFilters, filters, channel),
    );
  };

  if (isEmptyState) {
    return (
      <EmptyState
        title={intl.formatMessage({
          id:
            channel === "email"
              ? "Load.EmailMessageList.EmptyStateActiveRecipientEmailActivityStreamTitle"
              : "Load.EmailMessageList.EmptyStateActiveRecipientSmsActivityStreamTitle",
        })}
        description={intl.formatMessage({
          id:
            channel === "email"
              ? "Load.EmailMessageList.EmptyStateActiveRecipientEmailActivityStreamDescription"
              : "Load.EmailMessageList.EmptyStateActiveRecipientSmsActivityStreamDescription",
        })}
      />
    );
  }

  return (
    <Flex flexDirection="column" gap="16px" margin="16px 0 0 0">
      <Filters
        idRecipient={item.Id}
        loading={value.loading}
        channel={channel}
      />

      <RenderWhen condition={hasNoResults}>
        <NoResults
          text={intl.formatMessage({
            id: "Load.ActivityStream.NoResultTitle",
          })}
        />
      </RenderWhen>

      <RenderWhen condition={!hasNoResults}>
        <StyledSection>
          {value.loading &&
            Array.from({ length: value.pageSize }).map((_, key) => (
              <DataListRow key={key}>
                <Grid cols={12}>
                  <GridCell span={3}>
                    <SkeletonLoader variant={SKELETON_LOADER_VARIANT_ONWHITE} />
                  </GridCell>
                  <GridCell span={7}>
                    <SkeletonLoader variant={SKELETON_LOADER_VARIANT_ONWHITE} />
                  </GridCell>
                  <GridCell span={2}>
                    <SkeletonLoader variant={SKELETON_LOADER_VARIANT_ONWHITE} />
                  </GridCell>
                </Grid>
              </DataListRow>
            ))}

          {!value.loading &&
            value.items.map((activity, key) => (
              <DataListRow key={key}>
                <ActivityStreamItem
                  recipient={item}
                  activity={activity}
                  channel={channel}
                />
              </DataListRow>
            ))}
        </StyledSection>
        {!value.loading && (
          <Flex justifyContent="space-between">
            <Pagination
              pageSize={value.pageSize}
              page={value.pageNumber}
              totalCount={value.totalItems}
              formatValues={intl.formatNumber}
              onChange={handlePageChange}
            />

            <div />
          </Flex>
        )}
      </RenderWhen>

      <Flex padding="16px 0 0 0">
        <DataRetentionMessage channel={channel} />
      </Flex>
    </Flex>
  );
};

export default TabActivityStream;
